// AGC: Recommendation model service — 3-level fallback: online → localStorage → bundle
import { getBackendUrl } from '../utils/backendUrl'
import { DEFAULT_ZARC_WEIGHTS } from '../types/admin/models'

const LS_KEY = 'droidfarm_recommendation_model'

export interface RecommendationModel {
  versionId: string | null
  cropRequirements: Record<string, any>
  zarcWeights: { precipitation: number; temperature: number; radiation: number; season: number }
  taskTemplates: Record<string, any>
  yieldModels: Record<string, any>
  source: 'online' | 'localStorage' | 'bundle'
}

// AGC: Bundle-level hardcoded fallback (level 3)
const BUNDLED_ZARC_WEIGHTS = { ...DEFAULT_ZARC_WEIGHTS }

const BUNDLED_MODEL: RecommendationModel = {
  versionId: null,
  cropRequirements: {},
  zarcWeights: BUNDLED_ZARC_WEIGHTS,
  taskTemplates: {},
  yieldModels: {},
  source: 'bundle',
}

export const fetchActiveModel = async (): Promise<RecommendationModel | null> => {
  try {
    const backendUrl = getBackendUrl()
    const res = await fetch(`${backendUrl}/api/v1/admin/config/recommendation-models?active=true`, {
      headers: { 'Authorization': 'Bearer admin-token', 'Content-Type': 'application/json' },
    })
    if (!res.ok) return null
    const data = await res.json()
    const versions = data.data?.versions || []
    if (versions.length === 0) return null
    const v = versions[0]
    return {
      versionId: v.versionId,
      cropRequirements: v.cropRequirements || {},
      zarcWeights: v.zarcWeights || BUNDLED_ZARC_WEIGHTS,
      taskTemplates: v.taskTemplates || {},
      yieldModels: v.yieldModels || {},
      source: 'online',
    }
  } catch {
    return null
  }
}

export const seedLocalModel = (model: RecommendationModel): void => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(model))
  } catch {
    console.warn('AGC: Failed to seed recommendation model to localStorage')
  }
}

export const getLocalModel = (): RecommendationModel | null => {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return { ...parsed, source: 'localStorage' as const }
  } catch {
    return null
  }
}

export const getBundledModel = (): RecommendationModel => ({ ...BUNDLED_MODEL })

export const resolveModel = async (): Promise<RecommendationModel> => {
  // AGC: Level 1 — online
  const online = await fetchActiveModel()
  if (online) {
    seedLocalModel(online)
    console.log('AGC: [MODEL] Resolved from online (version:', online.versionId, ')')
    return online
  }

  // AGC: Level 2 — localStorage
  const local = getLocalModel()
  if (local) {
    console.log('AGC: [MODEL] Resolved from localStorage (version:', local.versionId, ')')
    return local
  }

  // AGC: Level 3 — bundle
  console.log('AGC: [MODEL] Resolved from bundle (hardcoded defaults)')
  return getBundledModel()
}

// AGC: Call after login to seed localStorage
export const syncModelOnLogin = async (): Promise<void> => {
  const model = await fetchActiveModel()
  if (!model) return
  const local = getLocalModel()
  if (!local || local.versionId !== model.versionId) {
    seedLocalModel(model)
    console.log('AGC: [MODEL] Synced model to localStorage (version:', model.versionId, ')')
  }
}
